import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { categoryPredictionApi, matchdayApi, predictionApi, teamApi } from '@/api/endpoints';
import type {
  Match,
  Matchday,
  MatchdayOverview,
  OverviewTips,
  Player,
  Prediction,
  Question,
  QuestionAnswer,
  QuestionSolution,
  Team,
} from '@/api/types';
import { Alert, Button, Card, Notice, Spinner } from '@/components/ui';
import { useAuth } from '@/auth/AuthContext';
import { BonusMatchdayPage } from '@/features/bonus/BonusMatchdayPage';
import { QuestionsBlock } from '@/features/questions/QuestionsBlock';
import { formatDateTime, isPastDeadline } from '@/lib/datetime';
import { pickActiveMatchday, sortChronologically } from '@/lib/matchday';
import { useAsync } from '@/lib/useAsync';
import { AttendanceSelect, MatchCategoryRow } from './CategoryInputs';
import {
  NONE_VALUE,
  playerValueToId,
  type AttendanceDraft,
  type PlayerDraft,
} from './categoryDraft';
import { MatchdayNavigator } from './MatchdayNavigator';
import { MatchRow, type PredictionDraft } from './MatchRow';
import styles from './ergebnistipp.module.css';

interface LoadedData {
  matchday: Matchday;
  matches: Match[];
  predictions: Prediction[];
  players: Player[];
  /** Eigene Kategorie-Tipps (aus der Overview, eigene Werte immer sichtbar). */
  ownTips: OverviewTips | null;
  /** Individuelle Fragen dieses Spieltags (i.d.R. leer — opt-in per Admin). */
  questions: Question[];
  questionAnswers: QuestionAnswer[];
  questionSolution: QuestionSolution[];
  questionsDeadlinePassed: boolean;
  teams: Team[];
}

export function MatchdayDetailPage() {
  const { id } = useParams<{ id: string }>();
  const matchdayId = Number(id);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Alle Spieltage laden — für den Reiter-Wechsel (Spieltag ↔ Bonusfragen) und
  // die Vor/Zurück-Navigation innerhalb des Spieltag-Reiters.
  const head = useAsync<Matchday[]>((signal) => matchdayApi.list(signal), []);

  if (Number.isNaN(matchdayId)) return <Alert>Ungültiger Spieltag.</Alert>;
  if (head.loading) return <Spinner label="Spieltag wird geladen…" />;
  if (head.error) return <Alert>{head.error}</Alert>;
  if (!head.data) return <Alert>Keine Daten.</Alert>;

  const matchday = head.data.find((m) => m.id === matchdayId);
  if (!matchday) return <Alert>Spieltag nicht gefunden.</Alert>;

  const navMatchdays = sortChronologically(head.data.filter((m) => m.kind !== 'bonus'));
  const bonusMatchday = sortChronologically(head.data.filter((m) => m.kind === 'bonus'))[0] ?? null;

  const isBonus = matchday.kind === 'bonus';

  const showResultTab = () => {
    const target = pickActiveMatchday(navMatchdays);
    if (target) navigate(`/matchdays/${target.id}`);
  };
  const showBonusTab = () => {
    if (bonusMatchday) navigate(`/matchdays/${bonusMatchday.id}`);
  };

  return (
    <>
      <h1>Tippabgabe</h1>
      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tab} ${!isBonus ? styles.tabActive : ''}`}
          disabled={navMatchdays.length === 0}
          onClick={showResultTab}
        >
          Spieltag
        </button>
        <button
          type="button"
          className={`${styles.tab} ${isBonus ? styles.tabActive : ''}`}
          disabled={!bonusMatchday}
          onClick={showBonusTab}
        >
          Bonusfragen
        </button>
      </div>

      {isBonus ? (
        <BonusMatchdayPage matchday={matchday} />
      ) : (
        <>
          <MatchdayNavigator current={matchday} all={navMatchdays} />
          <ResultMatchday matchday={matchday} userId={user?.id} />
        </>
      )}
    </>
  );
}

/** Lädt + rendert den klassischen Ergebnistipp-Spieltag (liga/pokal). */
function ResultMatchday({ matchday, userId }: { matchday: Matchday; userId?: number }) {
  const matchdayId = matchday.id;
  const { data, loading, error } = useAsync<LoadedData>(
    async (signal) => {
      const [matches, predictions, players, overview, questionsResp, teams] = await Promise.all([
        matchdayApi.matches(matchdayId, signal),
        predictionApi.listForMatchday(matchdayId, signal),
        // Kader ist optional (Backend evtl. noch nicht da) — defensiv.
        matchdayApi.players(matchdayId, signal).catch(() => [] as Player[]),
        matchdayApi.overview(matchdayId, signal).catch(() => null),
        matchdayApi.questions(matchdayId, signal),
        // Wie BonusMatchdayPage: nur Liga-Teams im Fragen-Dropdown (keine reinen Pokal-Gegner).
        teamApi.listLeague(signal).catch(() => [] as Team[]),
      ]);
      const ownTips = pickOwnTips(overview, userId);
      return {
        matchday,
        matches,
        predictions,
        players,
        ownTips,
        questions: [...questionsResp.questions].sort((a, b) => a.sort_order - b.sort_order),
        questionAnswers: questionsResp.my_answers ?? [],
        questionSolution: questionsResp.solution ?? [],
        questionsDeadlinePassed: questionsResp.deadline_passed ?? isPastDeadline(matchday.deadline_at),
        teams,
      };
    },
    [matchdayId, userId],
  );

  if (loading) return <Spinner label="Spieltag wird geladen…" />;
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <Alert>Keine Daten.</Alert>;

  return <MatchdayForm data={data} />;
}

/** Eigene Tipp-Werte aus der Overview ziehen (eigene Werte sind immer gefüllt). */
function pickOwnTips(overview: MatchdayOverview | null, userId?: number): OverviewTips | null {
  if (!overview || userId == null) return null;
  return overview.overview.find((e) => e.user_id === userId)?.tips ?? null;
}

/** Select-Wert aus einem flachen Spieler-Tipp ableiten (NULL = „keiner"). */
function playerDraftValue(tip: { player_id: number | null } | null | undefined): string {
  if (!tip) return '';
  return tip.player_id === null ? NONE_VALUE : String(tip.player_id);
}

function MatchdayForm({ data }: { data: LoadedData }) {
  const { matchday, matches, predictions, players, ownTips, questions, teams } = data;
  const locked = isPastDeadline(matchday.deadline_at);

  const pointsByMatch = useMemo(() => {
    const map = new Map<number, number | null>();
    for (const p of predictions) map.set(p.match_id, p.points);
    return map;
  }, [predictions]);

  const initialDrafts = useMemo<Record<number, PredictionDraft>>(() => {
    const drafts: Record<number, PredictionDraft> = {};
    for (const m of matches) {
      const existing = predictions.find((p) => p.match_id === m.id);
      drafts[m.id] = {
        home: existing ? String(existing.home_pred) : '',
        away: existing ? String(existing.away_pred) : '',
      };
    }
    return drafts;
  }, [matches, predictions]);

  // Kategorie-Drafts (Tor/Assist) aus eigenen Tipps initialisieren. Da jeder
  // Spieltag genau EIN Spiel hat, gelten scorer/assist für dieses eine Spiel.
  const initialPlayerDrafts = useMemo<Record<number, PlayerDraft>>(() => {
    const drafts: Record<number, PlayerDraft> = {};
    for (const m of matches) {
      drafts[m.id] = {
        scorer: playerDraftValue(ownTips?.scorer),
        assist: playerDraftValue(ownTips?.assist),
      };
    }
    return drafts;
  }, [matches, ownTips]);

  const initialAttendance = useMemo<AttendanceDraft>(
    () => ({
      interval:
        ownTips?.attendance?.interval_index != null
          ? String(ownTips.attendance.interval_index)
          : '',
    }),
    [ownTips],
  );

  const [drafts, setDrafts] = useState(initialDrafts);
  const [playerDrafts, setPlayerDrafts] = useState(initialPlayerDrafts);
  const [attendance, setAttendance] = useState(initialAttendance);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => setDrafts(initialDrafts), [initialDrafts]);
  useEffect(() => setPlayerDrafts(initialPlayerDrafts), [initialPlayerDrafts]);
  useEffect(() => setAttendance(initialAttendance), [initialAttendance]);

  const resetMsgs = useCallback(() => {
    setSavedMsg(null);
    setSaveError(null);
  }, []);

  const handleChange = useCallback(
    (matchId: number, side: 'home' | 'away', value: string) => {
      resetMsgs();
      setDrafts((prev) => {
        const current = prev[matchId] ?? { home: '', away: '' };
        return { ...prev, [matchId]: { ...current, [side]: value } };
      });
    },
    [resetMsgs],
  );

  const handlePlayerChange = useCallback(
    (matchId: number, field: 'scorer' | 'assist', value: string) => {
      resetMsgs();
      setPlayerDrafts((prev) => {
        const current = prev[matchId] ?? { scorer: '', assist: '' };
        return { ...prev, [matchId]: { ...current, [field]: value } };
      });
    },
    [resetMsgs],
  );

  const completeMatchIds = useMemo(
    () =>
      matches
        .filter((m) => {
          const d = drafts[m.id];
          return d && d.home !== '' && d.away !== '';
        })
        .map((m) => m.id),
    [matches, drafts],
  );

  const hasPlayers = players.length > 0;

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSavedMsg(null);
    try {
      let saved = 0;
      // Ergebnistipps.
      for (const matchId of completeMatchIds) {
        const d = drafts[matchId];
        if (!d || d.home === '' || d.away === '') continue;
        await predictionApi.upsert(matchId, {
          home_pred: Number(d.home),
          away_pred: Number(d.away),
        });
        saved += 1;
      }
      // Zuschauertipp (pro Spieltag genau einer).
      if (attendance.interval !== '') {
        await categoryPredictionApi.putAttendance(matchday.id, {
          interval_index: Number(attendance.interval),
        });
        saved += 1;
      }
      // Tor/Assist je Spiel (nur gewählte abschicken).
      for (const m of matches) {
        const pd = playerDrafts[m.id];
        if (!pd) continue;
        if (pd.scorer !== '') {
          await categoryPredictionApi.putScorer(m.id, { player_id: playerValueToId(pd.scorer) });
          saved += 1;
        }
        if (pd.assist !== '') {
          await categoryPredictionApi.putAssist(m.id, { player_id: playerValueToId(pd.assist) });
          saved += 1;
        }
      }
      setSavedMsg(saved === 0 ? 'Keine vollständigen Tipps.' : `${saved} Tipp(s) gespeichert.`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        setSaveError('Tippschluss überschritten — Tipps sind gesperrt.');
      } else {
        setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <p className="muted">Tippschluss: {formatDateTime(matchday.deadline_at)}</p>

      {locked && (
        <Notice>
          Der Tippschluss ist überschritten. Du kannst keine Tipps mehr abgeben oder ändern.
        </Notice>
      )}

      {matches.length === 0 && <Alert>Für diesen Spieltag sind keine Spiele hinterlegt.</Alert>}

      {matches.map((m) => (
        <div key={m.id}>
          <MatchRow
            match={m}
            draft={drafts[m.id] ?? { home: '', away: '' }}
            points={pointsByMatch.get(m.id) ?? null}
            locked={locked}
            isPokal={matchday.kind === 'pokal'}
            onChange={handleChange}
          />
          {hasPlayers && (
            <Card>
              <MatchCategoryRow
                match={m}
                players={players}
                draft={playerDrafts[m.id] ?? { scorer: '', assist: '' }}
                locked={locked}
                onChange={handlePlayerChange}
              />
            </Card>
          )}
        </div>
      ))}

      {matches.length > 0 && (
        <Card>
          <AttendanceSelect
            value={attendance.interval}
            locked={locked}
            actualAttendance={matches[0]?.attendance ?? null}
            onChange={(interval) => {
              resetMsgs();
              setAttendance({ interval });
            }}
          />
        </Card>
      )}

      {!locked && matches.length > 0 && (
        <div className={styles.saveBar}>
          {saveError && <Alert>{saveError}</Alert>}
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Speichern…' : 'Tipps speichern'}
          </Button>
          {savedMsg && <div className={styles.savedHint}>{savedMsg}</div>}
        </div>
      )}

      {questions.length > 0 && (
        <QuestionsBlock
          showHeader={false}
          data={{
            matchday,
            questions,
            myAnswers: data.questionAnswers,
            solution: data.questionSolution,
            deadlinePassed: data.questionsDeadlinePassed,
            teams,
          }}
        />
      )}
    </>
  );
}
